import type { FC, HTMLAttributes } from "react";
import { cn } from "@/utils/cn";

export interface DomainBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Domain text to display
   */
  domain: string;
  /**
   * Optional variant of the badge
   */
  variant?: "primary" | "secondary" | "accent";
  /**
   * Optional size of the badge
   */
  size?: "sm" | "md" | "lg";
  /**
   * Whether to make the badge mono-spaced
   */
  mono?: boolean;
  /**
   * Whether to show the badge on mobile
   */
  showOnMobile?: boolean;
}

/**
 * A badge component for displaying domain names
 */
export const DomainBadge: FC<DomainBadgeProps> = ({
  domain,
  variant = "primary",
  size = "md",
  mono = true,
  showOnMobile = true,
  className,
  ...props
}) => {
  // Map variant to classes
  const variantClasses = {
    accent: "bg-theme-accent/10 text-theme-accent",
    primary: "bg-theme-primary/10 text-theme-primary",
    secondary: "bg-theme-secondary/10 text-theme-secondary",
  };

  // Map size to classes
  const sizeClasses = {
    lg: "text-sm py-1 px-2.5",
    md: "text-xs py-0.5 px-2",
    sm: "text-xs py-0.5 px-1.5",
  };

  // Font classes
  const fontClasses = mono ? "font-mono" : "font-medium";

  // Responsive classes
  const responsiveClasses = showOnMobile ? "" : "hidden sm:inline-flex";

  return (
    <span
      className={cn(
        "rounded-full inline-flex items-center",
        "group-hover:bg-opacity-20",
        "select-none",
        variantClasses[variant],
        sizeClasses[size],
        fontClasses,
        responsiveClasses,
        className,
      )}
      data-testid="domain-badge"
      {...props}
    >
      {domain}
    </span>
  );
};
